import 'rc-slider/assets/index.css'

import { loyaltyProgramNames } from 'components/ReusableComponents/Arrivals/LoyaltyIcon/loyaltyLevels'
import { PrimaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import { PrimarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import MultiSelectDropdown from 'components/ReusableComponents/MultiSelectDropdown'
import SmartDatepicker from 'components/ReusableComponents/SmartDatepicker'
import Toggle from 'components/ReusableComponents/Toggle'
import { audienceFilterTypes } from 'features/CustomCampaigns/Audience'
import moment from 'moment'
import Slider from 'rc-slider'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import canadianProvinces from 'static/addresses/canadianProvinces'
import usStates from 'static/addresses/usStates'

import CheckIcon from './Checkicon'
import styles from './FilterSidebar.module.scss'
import LoyaltyProgramSections from './LoyaltyProgramSections'

function stringToArray(str) {
  try {
    if (str.includes(',')) {
      return str.split(',')
    } else {
      return [str]
    }
  } catch (e) {
    return []
  }
}

export const TogglePill = ({ checked, children, onChange }) => (
  <label className={checked ? styles.togglePillChecked : styles.togglePill}>
    <input
      type="checkbox"
      className={styles.togglePillCheckbox}
      checked={checked}
      onChange={e => onChange(e.target.checked)}
    />
    <div className={styles.togglePillItself}>
      <span className={styles.togglePillText}>{children}</span>
      {checked && <CheckIcon />}
    </div>
  </label>
)

const FilterSidebar = ({
  handleClickFilterButton,
  filterState,
  dispatchFilterState,
  onSave
}) => {
  const {
    rateCodes,
    bookingSource,
    membership,
    nightlyRate,
    minStays,
    maxStays,
    distanceFromHotel,
    checkoutTo,
    checkoutFrom,
    isSaving,
    reviewRating,
    states,
    futureStayDateTo,
    futureStayDateFrom
  } = filterState

  const setCheckoutTo = payload =>
    dispatchFilterState({ type: audienceFilterTypes.checkoutTo, payload })

  const setCheckoutFrom = payload => {
    dispatchFilterState({ type: audienceFilterTypes.checkoutFrom, payload })
  }

  const setFutureStayDateTo = payload =>
    dispatchFilterState({ type: audienceFilterTypes.futureStayDateTo, payload })

  const setFutureStayDateFrom = payload =>
    dispatchFilterState({ type: audienceFilterTypes.futureStayDateFrom, payload })

  const setReviewRating = payload => {
    dispatchFilterState({ type: audienceFilterTypes.reviewRating, payload })
  }
  const setDistanceFromHotel = payload =>
    dispatchFilterState({ type: audienceFilterTypes.distanceFromHotel, payload })

  const setNumberOfStays = payload =>
    dispatchFilterState({ type: audienceFilterTypes.numberOfStays, payload })

  const setNightlyRate = payload =>
    dispatchFilterState({ type: audienceFilterTypes.nightlyRate, payload })

  const setMembership = payload =>
    dispatchFilterState({ type: audienceFilterTypes.membership, payload })

  const setBookingSource = payload =>
    dispatchFilterState({ type: audienceFilterTypes.bookingSource, payload })

  const setStates = payload =>
    dispatchFilterState({ type: audienceFilterTypes.states, payload })

  const parentChain = useSelector(s => s.hotelDetails.parentChainCode)

  const [activeTab, setActiveTab] = useState(futureStayDateFrom ? 'future' : 'past')

  useEffect(() => {
    const hasLoyaltyProgram = loyaltyProgramNames[parentChain]
    const setMembership = payload =>
      dispatchFilterState({ type: audienceFilterTypes.membership, payload })

    if (hasLoyaltyProgram && (membership.loyalties === null || membership === null)) {
      setMembership({
        ...membership,
        loyalties: []
      })
    }
  }, [parentChain, dispatchFilterState, membership])

  useEffect(() => {
    if (reviewRating === null) {
      dispatchFilterState({
        type: audienceFilterTypes.reviewRating,
        payload: 'NoRating,1,2,3,4,5'
      })
    }
  }, [dispatchFilterState, reviewRating])

  function getNumberOfStaysText(minStays, maxStays) {
    if (maxStays > 11 && minStays === 0) {
      return `Any number of stays`
    }

    if (maxStays > 11) {
      return `More than ${minStays} stays`
    }

    return `Between ${minStays}-${maxStays} stays`
  }

  function getDistanceFromHotelText(distanceFromHotel) {
    const distance = distanceFromHotel === null ? 1500 : distanceFromHotel

    return distance > 1500 ? 'Any distance' : `Within ${distance} miles`
  }

  function handleChangeUseDates(e) {
    if (e.target.checked) {
      setCheckoutFrom(moment().add(-60, 'days').format('YYYY-MM-DD'))
      setCheckoutTo(moment().add(-30, 'days').format('YYYY-MM-DD'))
    } else {
      setCheckoutFrom(null)
      setCheckoutTo(null)
    }
  }

  function handleClickPastTab() {
    setActiveTab('past')
    setFutureStayDateFrom(null)
    setFutureStayDateTo(null)
  }

  function handleClickFutureTab() {
    setActiveTab('future')
    setCheckoutFrom(null)
    setCheckoutTo(null)
    setFutureStayDateFrom(moment().add(30, 'days').format('YYYY-MM-DD'))
    setFutureStayDateTo(moment().add(31, 'days').format('YYYY-MM-DD'))
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabs}>
        <button
          className={activeTab === 'past' ? styles.tabActive : styles.tab}
          onClick={handleClickPastTab}
        >
          Past guests
        </button>
        <button
          className={activeTab === 'future' ? styles.tabActive : styles.tab}
          onClick={handleClickFutureTab}
        >
          Future guests
        </button>
      </div>
      <div className={styles.controlGroupWithFloat}>
        <div>
          <p className={styles.filterTitle}>Rate codes</p>
          <p className={styles.filterDescription}>All rate codes are included </p>
        </div>
        <PrimarySmallButton
          className={styles.rateCodeCta}
          onClick={handleClickFilterButton}
        >
          Select rate codes {rateCodes.length ? ' (' + rateCodes.length + ')' : ''}
        </PrimarySmallButton>
      </div>
      {activeTab === 'past' && (
        <div className={styles.controlGroup}>
          <div style={{ display: 'flex' }}>
            <div>
              <p className={styles.filterTitle}>Checkout dates</p>
              <p className={styles.filterDescription}>All checkout dates are included </p>
            </div>
            <Toggle
              className={styles.checkoutDateToggle}
              label="Specify date range"
              checked={checkoutTo !== null}
              handleChange={handleChangeUseDates}
            />
          </div>
          {checkoutFrom && (
            <div className={styles.datepickerBox}>
              <SmartDatepicker
                className={styles.datepicker}
                isRange
                onChangeStartDate={date => setCheckoutFrom(date.format('YYYY-MM-DD'))}
                onChangeEndDate={date => setCheckoutTo(date.format('YYYY-MM-DD'))}
                startDate={moment(checkoutFrom)}
                endDate={moment(checkoutTo)}
                maxDate={moment().add(-30, 'days')}
                hasSmartOptions={false}
              />
            </div>
          )}
        </div>
      )}
      {activeTab === 'future' && (
        <div className={styles.controlGroup}>
          <div style={{ display: 'flex' }}>
            <div>
              <p className={styles.filterTitle}>Guests will be staying within</p>
            </div>
          </div>
          <div className={styles.datepickerBoxFuture}>
            <SmartDatepicker
              className={styles.datepicker}
              isRange
              onChangeStartDate={date => setFutureStayDateFrom(date.format('YYYY-MM-DD'))}
              onChangeEndDate={date => setFutureStayDateTo(date.format('YYYY-MM-DD'))}
              startDate={moment(futureStayDateFrom)}
              endDate={moment(futureStayDateTo)}
              minDate={moment().add(1, 'days')}
              maxDate={moment().add(2, 'years')}
              hasSmartOptions={false}
            />
          </div>
        </div>
      )}
      <div className={styles.controlGroup}>
        <div>
          <p className={styles.filterTitle}>Booking source</p>
        </div>
        <div className={styles.pillHolder}>
          <TogglePill
            onChange={checked =>
              checked
                ? bookingSource.includes('Direct')
                  ? setBookingSource(null)
                  : setBookingSource('OTA')
                : bookingSource === 'OTA'
                ? false
                : setBookingSource('Direct')
            }
            checked={bookingSource === null ? true : bookingSource.includes('OTA')}
          >
            OTA
          </TogglePill>
          <TogglePill
            checked={bookingSource === null ? true : bookingSource.includes('Direct')}
            onChange={checked =>
              checked
                ? bookingSource.includes('Ota')
                  ? setBookingSource(null)
                  : setBookingSource('Direct')
                : bookingSource === 'Direct'
                ? false
                : setBookingSource('OTA')
            }
          >
            Direct
          </TogglePill>
        </div>
      </div>
      <div className={styles.controlGroup}>
        <div>
          <p className={styles.filterTitle}>Nightly rate</p>
          <p className={styles.filterDescription}>
            Average of nightly rate of guests past stays
          </p>
        </div>
        <div className={styles.pillHolder}>
          <TogglePill
            onChange={checked => (checked ? setNightlyRate('0-100') : false)}
            checked={nightlyRate === '0-100'}
          >
            $0-100
          </TogglePill>
          <TogglePill
            onChange={checked => (checked ? setNightlyRate('100-200') : false)}
            checked={nightlyRate === '100-200'}
          >
            $100-200
          </TogglePill>
          <TogglePill
            onChange={checked => (checked ? setNightlyRate(null) : false)}
            checked={nightlyRate === null}
          >
            Any rate
          </TogglePill>
        </div>
      </div>

      <LoyaltyProgramSections
        membership={membership}
        setMembership={setMembership}
        parentChain={parentChain}
      />

      <div className={styles.controlGroup}>
        <div>
          <p className={styles.filterTitle}>State / Province</p>
        </div>
        <div style={{ marginTop: 10 }}>
          <MultiSelectDropdown
            options={{ ...usStates, ...canadianProvinces }}
            value={states}
            onChangeValue={setStates}
            itemName="state/province"
            itemNamePlural="states/provinces"
            hasUnselectButton
            zeroResultText={'Any state or province'}
          />
        </div>
      </div>
      <div className={styles.controlGroup}>
        <div>
          <p className={styles.filterTitle}>Review rating</p>
        </div>
        <div className={styles.pillHolder}>
          <TogglePill
            checked={stringToArray(reviewRating).includes('NoRating')}
            onChange={checked =>
              checked
                ? setReviewRating([...stringToArray(reviewRating), 'NoRating'].join(','))
                : setReviewRating(
                    stringToArray(reviewRating)
                      .filter(r => r !== 'NoRating')
                      .join(',')
                  )
            }
          >
            No rating
          </TogglePill>
          <TogglePill
            checked={stringToArray(reviewRating).includes('1')}
            onChange={checked =>
              checked
                ? setReviewRating([...stringToArray(reviewRating), '1'].join(','))
                : setReviewRating(
                    stringToArray(reviewRating)
                      .filter(r => r !== '1')
                      .join(',')
                  )
            }
          >
            1
          </TogglePill>
          <TogglePill
            checked={stringToArray(reviewRating).includes('2')}
            onChange={checked =>
              checked
                ? setReviewRating([...stringToArray(reviewRating), '2'].join(','))
                : setReviewRating(
                    stringToArray(reviewRating)
                      .filter(r => r !== '2')
                      .join(',')
                  )
            }
          >
            2
          </TogglePill>
          <TogglePill
            checked={stringToArray(reviewRating).includes('3')}
            onChange={checked =>
              checked
                ? setReviewRating([...stringToArray(reviewRating), '3'].join(','))
                : setReviewRating(
                    stringToArray(reviewRating)
                      .filter(r => r !== '3')
                      .join(',')
                  )
            }
          >
            3
          </TogglePill>
          <TogglePill
            checked={stringToArray(reviewRating).includes('4')}
            onChange={checked =>
              checked
                ? setReviewRating([...stringToArray(reviewRating), '4'].join(','))
                : setReviewRating(
                    stringToArray(reviewRating)
                      .filter(r => r !== '4')
                      .join(',')
                  )
            }
          >
            4
          </TogglePill>
          <TogglePill
            checked={stringToArray(reviewRating).includes('5')}
            onChange={checked =>
              checked
                ? setReviewRating([...stringToArray(reviewRating), '5'].join(','))
                : setReviewRating(
                    stringToArray(reviewRating)
                      .filter(r => r !== '5')
                      .join(',')
                  )
            }
          >
            5
          </TogglePill>
        </div>
      </div>
      <div className={styles.controlGroup}>
        <div>
          <p className={styles.filterTitle}>Number of stays in last 12 mo</p>
        </div>
        <div className={styles.sliderContainer}>
          <Slider
            className={styles.rangeSlider}
            range
            min={0}
            max={12}
            step={1}
            value={[minStays, maxStays]}
            onChange={([minStays, maxStays]) => setNumberOfStays({ minStays, maxStays })}
            styles={{
              track: {
                backgroundColor: '#4079F5',
                height: 5
              },
              rail: {
                backgroundColor: '#E6E6E6',
                height: 5
              },
              handle: {
                backgroundColor: '#4079F5',
                border: '3px solid white',
                width: 17,
                height: 17,
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.60)',
                marginTop: -6,
                opacity: 1
              },
              handleActive: {
                backgroundColor: '#4079F5',
                border: '4px solid white',
                width: 16,
                height: 16,
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.60)'
              }
            }}
          />
          <span className={styles.sliderValue}>
            {getNumberOfStaysText(minStays, maxStays)}
          </span>
        </div>
      </div>

      <div
        className={states.length > 0 ? styles.controlGroupDisabled : styles.controlGroup}
      >
        <div>
          <p className={styles.filterTitle}>Distance from hotel</p>
        </div>
        <div className={styles.sliderContainer}>
          <Slider
            className={styles.rangeSlider}
            min={50}
            max={1500}
            step={50}
            value={distanceFromHotel === null ? 1500 : distanceFromHotel}
            onChange={x => setDistanceFromHotel(x === 1500 ? null : x)}
            styles={{
              track: {
                backgroundColor: '#4079F5',
                height: 5
              },
              rail: {
                backgroundColor: '#E6E6E6',
                height: 5
              },
              handle: {
                backgroundColor: '#4079F5',
                border: '3px solid white',
                width: 17,
                height: 17,
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.60)',
                marginTop: -6,
                opacity: 1
              },
              handleActive: {
                backgroundColor: '#4079F5',
                border: '4px solid white',
                width: 16,
                height: 16,
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.60)'
              }
            }}
          />
          <span className={styles.sliderValue}>
            {getDistanceFromHotelText(distanceFromHotel)}
          </span>
        </div>
      </div>
      <div className={styles.saveButtonHolder}>
        <PrimaryRegularButton
          className={styles.saveButton}
          disabled={isSaving}
          onClick={() => onSave()}
        >
          {isSaving ? 'Updating audience...' : 'Update audience'}
        </PrimaryRegularButton>
      </div>
    </div>
  )
}

export default FilterSidebar
